// Texas Heat Map — stylized silhouette + clickable city dots and drill-down rows.
// Clicking a city → onPick(cityName) → parent applies a global city filter.
import { MapPin } from "lucide-react";
import { RED } from "./shared";

// Order matters: smaller / suburb dots first, major hubs (Dallas, Houston, Austin) LAST
// so they render on top of their cluster and are clickable directly.
const TX_CITIES = [
  // DFW suburbs — fanned out around Dallas to prevent overlap on click
  { slug: "denton",      name: "Denton",       x: 54, y: 26 },
  { slug: "frisco",      name: "Frisco",       x: 60, y: 26 },
  { slug: "mckinney",    name: "McKinney",     x: 66, y: 26 },
  { slug: "lewisville",  name: "Lewisville",   x: 56, y: 31 },
  { slug: "plano",       name: "Plano",        x: 64, y: 31 },
  { slug: "allen",       name: "Allen",        x: 70, y: 30 },
  { slug: "garland",     name: "Garland",      x: 68, y: 36 },
  { slug: "rockwall",    name: "Rockwall",     x: 72, y: 39 },
  { slug: "fort-worth",  name: "Fort Worth",   x: 52, y: 41 },
  // Major hubs render last → on top
  { slug: "dallas",      name: "Dallas",       x: 62, y: 38 },
  { slug: "austin",      name: "Austin",       x: 55, y: 64 },
  { slug: "houston",     name: "Houston",      x: 72, y: 70 },
];

export function TexasHeatMap({ cities, onPick, activeCity }) {
  const max = Math.max(1, ...cities.map((c) => c.leads));
  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid="heat-map">
      <div className="px-5 py-3 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2">
          <MapPin size={14} color={RED} /> TEXAS LIVE HEAT MAP
        </p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">
          {cities.length} cities · click any city to filter all views
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-5">
        <div className="lg:col-span-2 relative bg-black/40 rounded-sm border border-white/5 aspect-[4/3]">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path
              d="M 12,18 L 32,16 L 42,12 L 56,15 L 62,14 L 64,18 L 72,18 L 72,30 L 80,32 L 84,40 L 88,52 L 86,62 L 78,72 L 72,82 L 64,86 L 56,90 L 48,86 L 44,90 L 36,86 L 30,72 L 20,62 L 14,52 L 10,38 Z"
              fill="rgba(255,42,60,0.05)"
              stroke="rgba(255,42,60,0.6)"
              strokeWidth="0.5"
            />
            {/* Pass 1: animated halos (no pointer events — purely decorative). */}
            {TX_CITIES.map((c) => {
              const row = cities.find((cc) => cc.city?.toLowerCase() === c.name.toLowerCase());
              const leads = row?.leads || 0;
              const intensity = leads / max;
              const r = 1.2 + intensity * 3.2;
              const isActive = activeCity && activeCity.toLowerCase() === c.name.toLowerCase();
              return (
                <circle key={`halo-${c.slug}`} cx={c.x} cy={c.y} r={r + (isActive ? 4 : 2.5)}
                  fill={RED} opacity={(isActive ? 0.45 : 0.15) + intensity * 0.35}
                  pointerEvents="none">
                  <animate attributeName="r" values={`${r + 1.5};${r + 4};${r + 1.5}`} dur="2.4s" repeatCount="indefinite" />
                </circle>
              );
            })}
            {/* Pass 2: static visible dots + labels + invisible hit area (stable for clicks). */}
            {TX_CITIES.map((c) => {
              const row = cities.find((cc) => cc.city?.toLowerCase() === c.name.toLowerCase());
              const leads = row?.leads || 0;
              const intensity = leads / max;
              const r = 1.2 + intensity * 3.2;
              const isActive = activeCity && activeCity.toLowerCase() === c.name.toLowerCase();
              return (
                <g key={c.slug} className="cursor-pointer">
                  <circle cx={c.x} cy={c.y} r={r} fill={leads ? RED : "#444"} stroke="white" strokeWidth={isActive ? 0.6 : 0.3} pointerEvents="none" />
                  <text x={c.x + r + 1} y={c.y + 0.8} fill="white" fontSize="2.2" fontWeight="800" pointerEvents="none">{c.name}</text>
                  {leads > 0 && <text x={c.x + r + 1} y={c.y + 3.6} fill={RED} fontSize="2" fontWeight="900" pointerEvents="none">{leads}</text>}
                  {/* Invisible static hit area — receives the click, owns the testid. */}
                  <circle cx={c.x} cy={c.y} r={Math.max(2.2, r + 0.5)} fill="transparent"
                    onClick={() => onPick && onPick(c.name, row)}
                    data-testid={`heat-dot-${c.slug}`} />
                </g>
              );
            })}
          </svg>
          <div className="absolute bottom-2 left-2 text-[10px] text-white/40 tracking-[0.18em] font-bold">PULSE INTENSITY = LEAD VOLUME</div>
        </div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          <p className="font-heavy text-[12px] tracking-[0.16em] text-white/55 mb-1">CITY DRILL-DOWN</p>
          {cities.length === 0 && <p className="text-white/45 text-[12px]">No city data yet.</p>}
          {cities.map((row) => {
            const isActive = activeCity && activeCity.toLowerCase() === row.city.toLowerCase();
            return (
              <button
                key={row.city}
                onClick={() => onPick && onPick(row.city, row)}
                data-testid={`heat-row-${row.city.toLowerCase()}`}
                className="w-full flex items-center justify-between px-3 py-2 rounded-sm border transition-colors"
                style={{
                  borderColor: isActive ? RED : "rgba(255,255,255,0.10)",
                  background: isActive ? "rgba(255,42,60,0.10)" : "rgba(0,0,0,0.30)",
                }}
              >
                <span className="font-extrabold text-[12px] text-white">{row.city}</span>
                <span className="font-heavy" style={{ color: RED, fontSize: "16px" }}>{row.leads}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
